// Closing a commitment — the verb behind the rail's BALL cell.
//
// A discharge is a RECORD, not a checkbox: it says when the promise was kept and cites the
// artefact that kept it. The server refuses everything else — no evidence (422), a date before
// the commitment or in the future (422), a second discharge (409) — and its refusals are
// quoted here verbatim rather than paraphrased into a shrug.
//
// Exactly one piece of evidence: a document from THIS client's library, or another event on
// THIS client's timeline. The form enforces the "exactly one" so the server never has to.
import { useState } from 'react';
import { useToast } from './Toast.js';
import { useLocale } from '../i18n/index.js';
import { timelineApi, type EventEntry } from '../api/timeline.js';
import type { LibraryDocumentRow } from '../api/documents.js';
import './DischargeModal.de.js';
import './DischargeModal.fr.js';

interface Props {
  open: boolean;
  commitment: EventEntry | null;
  /** This client's library — the discharging document must come from it. */
  documents: LibraryDocumentRow[];
  /** This client's other events — the discharging event must be one of them. */
  events: EventEntry[];
  onClose: () => void;
  onDischarged: () => void;
}

const today = () => new Date().toISOString().slice(0, 10);

export default function DischargeModal({ open, commitment, documents, events, onClose, onDischarged }: Props) {
  const toast = useToast();
  const { t, fmtDate } = useLocale();
  const [kind, setKind] = useState<'document' | 'event'>('document');
  const [ref, setRef] = useState('');
  const [date, setDate] = useState(today());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open || !commitment) return null;

  async function submit() {
    if (!commitment) return;
    setSaving(true);
    try {
      await timelineApi.discharge(commitment.id, {
        discharged_at: date,
        ...(kind === 'document' ? { discharged_by_document_ref: ref } : { discharged_by_event_ref: ref }),
        discharge_note: note.trim() || null,
      });
      toast('ok', t('{ref} discharged — the ball moves.', { ref: commitment.ref }));
      onDischarged();
      onClose();
      setRef('');
      setNote('');
      setDate(today());
    } catch (err) {
      // 422 carries the corrective message; 409 says it was already discharged, and by what.
      toast('err', (err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const candidates = events.filter((e) => e.id !== commitment.id);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{t('Discharge {ref}', { ref: commitment.ref })}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* the promise, in the record's own voice, with its ref adjacent */}
          <div className="ev">{commitment.title}</div>
          <div className="ev-src">
            <span className="src-ref">{commitment.ref}</span>
            <span className="sep">·</span>
            <span className="voice-figure">{t('made {date}', { date: fmtDate(commitment.occurred_at) })}</span>
            <span className="sep">·</span>
            <span>{t('owner {owner}', { owner: commitment.commitment_owner ?? t('unrecorded') })}</span>
          </div>

          <div className="form-group" style={{ marginTop: 'var(--s16)' }}>
            <label className="form-label">{t('Discharged on — the date the promise was kept')}</label>
            <input className="form-input" type="date" style={{ width: 170 }} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">{t("Evidence — what kept it. Exactly one, and it must be this client's.")}</label>
            <div className="flex gap-8 mb-8">
              <button
                type="button"
                className={`btn btn-sm${kind === 'document' ? ' btn-accent' : ''}`}
                onClick={() => { setKind('document'); setRef(''); }}
              >
                {t('A document')}
              </button>
              <button
                type="button"
                className={`btn btn-sm${kind === 'event' ? ' btn-accent' : ''}`}
                onClick={() => { setKind('event'); setRef(''); }}
              >
                {t('An event')}
              </button>
            </div>
            <select className="form-select" value={ref} onChange={(e) => setRef(e.target.value)}>
              <option value="">
                {kind === 'document' ? t('— pick the document that kept the promise —') : t('— pick the event that kept the promise —')}
              </option>
              {kind === 'document'
                ? documents.map((d) => <option key={d.id} value={d.ref}>{d.ref} — {d.original_filename}</option>)
                : candidates.map((e) => <option key={e.id} value={e.ref}>{e.ref} — {e.title}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">{t('Note — optional, one line on how it was kept')}</label>
            <input className="form-input" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn btn-accent" disabled={saving || !ref || !date} onClick={() => void submit()}>
            {saving ? t('Recording…') : t('Record the discharge')}
          </button>
        </div>
      </div>
    </div>
  );
}
